/**
 * feat-187 (R1) — client-side guardrail for a Lesson's free-form emoji.
 *
 * `learn_lesson.emoji` is VARCHAR(10) (declared in `021_quiz_system.sql` under
 * the table's pre-feat-198 name; the MIGRATION FILENAME does not change), i.e.
 * TEN CHARACTERS
 * as MySQL counts them in utf8mb4 — code points, not bytes. Most single glyphs
 * fit comfortably (🌮 = 1, 👋🏽 = 2, 🇺🇸 = 2, 🧑‍🍳 = 3, 🏳️‍🌈 = 4, 👨‍👩‍👧‍👦 = 7), but a
 * skin-toned family (👨🏻‍👩🏻‍👧🏻‍👦🏻 = 11) does not, and pasting two emoji at once
 * blows the column trivially. Left unchecked that is either a 500 from the write
 * or — worse, in a non-strict MySQL mode — a silently truncated half-glyph in
 * the navigator.
 *
 * So the Lesson picker's call site normalizes here BEFORE the patch: keep the
 * FIRST grapheme cluster the author typed, and refuse with a friendly message
 * when even that one cluster can't fit. Pure + synchronous; no React, no network.
 *
 * The Drill icon picker deliberately does NOT use this (its clearing behavior is
 * scoped out of feat-187) — this module is the Lesson glyph's guardrail only.
 */

/** Max code points `learn_lesson.emoji` can hold (VARCHAR(10), utf8mb4). */
export const UNIT_EMOJI_MAX_CODE_POINTS = 10;

/** Minimal structural type for `Intl.Segmenter` — TS's own lib types are gated
 *  behind a newer `lib` than this project's (`es5` target). `containing(0)` gives
 *  the first cluster without iterating (which `target: es5` would reject). */
type SegmenterCtor = new (
  locales?: string | string[],
  options?: { granularity?: 'grapheme' | 'word' | 'sentence' },
) => {
  segment: (input: string) => { containing: (index: number) => { segment: string } | undefined };
};

/**
 * The first grapheme cluster of `input` ('' for empty input). Uses
 * `Intl.Segmenter` where available (Node 16+/every browser we support) and falls
 * back to a ZWJ/modifier-aware scan otherwise, so a ZWJ sequence is never split
 * mid-glyph.
 */
export function firstGrapheme(input: string): string {
  const text = input.trim();
  if (!text) return '';

  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;
  if (typeof Segmenter === 'function') {
    const first = new Segmenter(undefined, { granularity: 'grapheme' })
      .segment(text)
      .containing(0);
    if (first) return first.segment;
  }
  return fallbackFirstGrapheme(text);
}

/** Code points that JOIN to the cluster they follow (so they never start one). */
function isJoiner(codePoint: number): boolean {
  return (
    codePoint === 0x200d || // ZERO WIDTH JOINER
    (codePoint >= 0xfe00 && codePoint <= 0xfe0f) || // variation selectors
    (codePoint >= 0x1f3fb && codePoint <= 0x1f3ff) || // skin-tone modifiers
    (codePoint >= 0xe0020 && codePoint <= 0xe007f) || // tag sequences (subdivision flags)
    codePoint === 0x20e3 // COMBINING ENCLOSING KEYCAP
  );
}

function isRegionalIndicator(codePoint: number): boolean {
  return codePoint >= 0x1f1e6 && codePoint <= 0x1f1ff;
}

function fallbackFirstGrapheme(text: string): string {
  const cps = Array.from(text);
  if (cps.length === 0) return '';

  let out = cps[0];
  let i = 1;
  // A country flag is exactly TWO regional indicators.
  if (
    isRegionalIndicator(cps[0].codePointAt(0)!) &&
    cps[1] &&
    isRegionalIndicator(cps[1].codePointAt(0)!)
  ) {
    out += cps[1];
    i = 2;
  }
  while (i < cps.length) {
    const cp = cps[i].codePointAt(0)!;
    if (!isJoiner(cp)) break;
    out += cps[i];
    i += 1;
    // A ZWJ binds the NEXT code point into this same cluster (🧑‍🍳).
    if (cp === 0x200d && i < cps.length) {
      out += cps[i];
      i += 1;
    }
  }
  return out;
}

export type UnitEmojiResult =
  | { ok: true; emoji: string; /** true when input carried more than one cluster */ trimmed: boolean }
  | { ok: false; reason: 'empty' | 'too_long'; message: string };

/**
 * Normalize author input into something `learn_lesson.emoji` can actually hold:
 * the first grapheme cluster, rejected with human-facing copy when it exceeds
 * {@link UNIT_EMOJI_MAX_CODE_POINTS}. `trimmed` tells the caller the author
 * typed more than one glyph so it can say so instead of silently dropping the rest.
 */
export function normalizeUnitEmoji(input: string): UnitEmojiResult {
  const raw = input.trim();
  if (!raw) {
    return { ok: false, reason: 'empty', message: 'Type an emoji first.' };
  }

  const emoji = firstGrapheme(raw);
  if (!emoji) {
    return { ok: false, reason: 'empty', message: 'Type an emoji first.' };
  }

  const codePoints = Array.from(emoji).length;
  if (codePoints > UNIT_EMOJI_MAX_CODE_POINTS) {
    return {
      ok: false,
      reason: 'too_long',
      message: `That emoji is too complex to store (it needs ${codePoints} characters, and we have room for ${UNIT_EMOJI_MAX_CODE_POINTS}). Try a simpler one — 🧑‍🍳 and 🇺🇸 both fit.`,
    };
  }

  return { ok: true, emoji, trimmed: emoji !== raw };
}
